// src/components/auth/AuthErrorNotice.tsx
//
// The login page's one error surface. It exists as a typed .tsx module rather
// than inline JSX in Login.jsx for two reasons:
//
//  1. The frontend tsconfig has `allowJs: true` WITHOUT `checkJs` and
//     `strict: false`, so nothing inside a .jsx file is type-checked. Anything
//     with a type or a branch belongs on this side of the boundary.
//  2. It is the seam a follow-up step swaps: the `not_authorized` case gets a
//     richer "your workspace has moved" card, and only this file changes.

import { Alert, Anchor, Stack, Text } from "@mantine/core";
import { IconExternalLink } from "@tabler/icons-react";
import { useAuth } from "@/context/AuthContext";
import type { AuthError } from "@/types/auth";

/**
 * Customer-facing copy per failure reason. Deliberately generic: this surface is
 * reached by an unauthenticated visitor, so it must never say anything about
 * whether an address exists or where it belongs.
 */
export function authErrorMessage(error: AuthError): string {
  switch (error.kind) {
    case "not_authorized":
      return "We couldn't sign you in with this account. If you think this is a mistake, contact your administrator.";
    case "network":
      return "We couldn't reach the sign-in service. Check your connection and try again.";
    default:
      return "Something went wrong while signing you in. Please try again.";
  }
}

export default function AuthErrorNotice() {
  const { authError } = useAuth();
  if (!authError) return null;

  // The account moved to a partner: say where, and link there. The destination
  // reaches us only from the server's ACCOUNT_MOVED body and has already passed
  // safeDestination() (https + a real hostname) before it becomes an href.
  if (authError.kind === "not_authorized" && authError.destination) {
    const { label, url } = authError.destination;
    return (
      <Alert
        color="blue"
        variant="light"
        role="alert"
        data-testid="auth-error-notice"
        data-auth-error-kind="account_moved"
        mb="md"
        styles={{
          root: {
            backgroundColor: "rgba(70, 120, 220, 0.18)",
            borderColor: "rgba(140, 180, 255, 0.4)",
          },
          message: { color: "rgba(225, 235, 255, 1)" },
          icon: { color: "rgba(180, 205, 255, 1)" },
        }}
      >
        <Stack gap={6}>
          <Text size="sm" fw={600}>
            Your workspace has moved to {label}
          </Text>
          <Text size="sm">
            This account is no longer available here. Sign in at {label} instead —
            your data and access are unchanged.
          </Text>
          <Anchor
            href={url}
            rel="noopener noreferrer"
            size="sm"
            fw={600}
            data-testid="auth-error-moved-link"
          >
            <IconExternalLink size={14} style={{ verticalAlign: "-2px" }} /> {url}
          </Anchor>
        </Stack>
      </Alert>
    );
  }

  return (
    <Alert
      color="red"
      variant="light"
      role="alert"
      data-testid="auth-error-notice"
      data-auth-error-kind={authError.kind}
      mb="md"
      styles={{
        root: {
          backgroundColor: "rgba(220, 53, 69, 0.18)",
          borderColor: "rgba(255, 120, 120, 0.4)",
        },
        message: { color: "rgba(255, 220, 220, 1)" },
        icon: { color: "rgba(255, 180, 180, 1)" },
      }}
    >
      {authErrorMessage(authError)}
    </Alert>
  );
}
